import { useEffect, useState } from "react";
import { api, type HealthResponse } from "../api/client";

/**
 * Polls /api/health on a 30s cadence. Returns the latest response or
 * null while the first request is in flight.
 *
 * /api/health currently returns { status, mode, version }; the
 * optional {@code commit} field is reserved for the deferred
 * git-SHA-in-health ops chore — once it lands, this hook surfaces
 * it automatically without a frontend change.
 */
export function useHealth(intervalMs = 30_000): HealthResponse | null {
  const [health, setHealth] = useState<HealthResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await api.health();
        if (!cancelled) setHealth(next);
      } catch {
        if (!cancelled) setHealth(null);
      }
    };
    void tick();
    const id = setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [intervalMs]);

  return health;
}
